import React, { useEffect, useState, createRef, useCallback } from 'react';
import { A } from 'components/Typography';
import { AnimatePresence } from 'framer-motion';
import { GLOBAL_NAV_HEIGHT } from 'consts';
import { TeamPageWrapper, JoinOurTeam, MembersList } from './styled';
import useFetchTeam from './useFetchTeam';
import Header from './components/Header';
import Map from './components/Map';
import TeamDirectory from './components/TeamDirectory';
import Overlay from './components/Overlay';
import CountryDirectory from './components/CountryDirectory';
import MemberDetails from './components/MemberDetails';

const SEARCH_HEIGHT = 45;

const filterByCategory = (team, category) => {
  return team.filter(
    (member) => member.categories && member.categories.find((x) => x === category)
  );
};

const getCountryNameWithoutCode = (country) => {
  const splitCountry = country.split('(');
  return splitCountry?.[0].trim();
};

const Team = () => {
  const { isLoading, team, error } = useFetchTeam();
  const [membersRefs, setMembersRefs] = useState([]);

  const [categories, setCategories] = useState([]);
  const [teamToShow, setTeamToShow] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [overlayHeight, setOverlayHeight] = useState(0);

  useEffect(() => {
    if (team && !isLoading) {
      const categoriesArray = [
        'All',
        'Founders',
        'Engineering',
        'Creative',
        'Growth',
        'Product',
        'Operations',
        'Finance',
        'Partnerships',
        'Customer Success'
      ];
      setCategories(categoriesArray);
      setTeamToShow(team);
      setMembersRefs((elRefs) =>
        Array(team.length)
          .fill()
          .map((_, i) => elRefs[i] || createRef())
      );
    }
  }, [team, isLoading]);

  useEffect(() => {
    if (selectedCountry) {
      setSelectedMembers(
        teamToShow.filter(
          (member) => getCountryNameWithoutCode(member.country) === selectedCountry
        )
      );
      return;
    }

    setSelectedMembers([]);
  }, [selectedCountry, teamToShow]);

  const onCategorySelect = (category) => {
    const isAll = category === 'All';
    setSelectedCategory(category);
    setTeamToShow(isAll ? team : filterByCategory(team, category));
  };

  const onCountrySelect = (country) => {
    setSelectedCountry(country);
    setSelectedMember(null);
  };

  const onMemberSelect = (member) => {
    setSelectedMember(member);
  };

  const scrollToMember = (member) => {
    const memberIndex = teamToShow?.indexOf(member);

    if (membersRefs[memberIndex]?.current) {
      window.scrollTo({
        top:
          membersRefs[memberIndex]?.current?.offsetTop -
          GLOBAL_NAV_HEIGHT -
          SEARCH_HEIGHT,
        behavior: 'smooth'
      });
    }
  };

  const closeOverlay = () => {
    setSelectedCountry(null);
    setSelectedMember(null);
  };

  const onRefChange = useCallback(
    (node) => {
      const nodeHeight = node?.getBoundingClientRect()?.height;

      if (nodeHeight) {
        setOverlayHeight(nodeHeight);
      }
    },
    [selectedMember, selectedCountry]
  );

  const renderOverlay = () => (
    <Overlay
      isOpen={Boolean(selectedMember || selectedMembers?.length)}
      onClose={closeOverlay}
      overlayHeight={overlayHeight}
    >
      <AnimatePresence exitBeforeEnter>
        {selectedMember && (
          <MemberDetails
            ref={onRefChange}
            key={selectedMember.id}
            teamMember={selectedMember}
            onClose={closeOverlay}
            onCountrySelect={onCountrySelect}
            selectedCountry={selectedCountry}
          />
        )}
        {selectedMembers?.length >= 1 && !selectedMember && selectedCountry && (
          <CountryDirectory
            key={`members${selectedMembers[0].id}`}
            teamMembers={selectedMembers}
            countryName={selectedCountry}
            onClose={closeOverlay}
            onMemberSelect={onMemberSelect}
            ref={onRefChange}
          />
        )}
      </AnimatePresence>
    </Overlay>
  );

  return (
    <TeamPageWrapper>
      {renderOverlay()}
      <Header
        title="Our team"
        categories={categories}
        team={team}
        selectedCategory={selectedCategory}
        onMemberSelect={onMemberSelect}
        onCategorySelect={onCategorySelect}
        scrollToMember={scrollToMember}
      />
      <Map onCountrySelect={onCountrySelect} team={teamToShow} />
      <Header
        title="A-Z"
        categories={categories}
        team={team}
        selectedCategory={selectedCategory}
        onCategorySelect={onCategorySelect}
        showSearch={false}
        hideOnMobile
      />
      <TeamDirectory
        teamMembers={teamToShow}
        error={error}
        isLoading={isLoading}
        onMemberSelect={onMemberSelect}
      />
      <MembersList>
        {teamToShow.map((member, i) => (
          <MemberDetails
            key={i}
            teamMember={member}
            scrollToMember={scrollToMember}
            ref={membersRefs[i]}
            showToggle
          />
        ))}
      </MembersList>
      <JoinOurTeam>
        Want to build a social safety net with us?{' '}
        <A href="https://safetywing.com/join-us">Join us</A>
      </JoinOurTeam>
    </TeamPageWrapper>
  );
};

export default Team;
